import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Search, Database, Zap } from 'lucide-react';
import { APIs } from '../config/apis';
import SearchPanel from '../components/SearchPanel';
import SmartSearchPanel from '../components/SmartSearchPanel';
import SearchResult from '../components/SearchResult';
import ResultsPanel from '../components/ResultsPanel';

const SearchPage = ({ 
  user,
  searchInputs, 
  setSearchInputs, 
  searchResults, 
  setSearchResults,
  loading, 
  searchCobranca, 
  searchAllApis,
  expandedResults,
  toggleExpandedResult
}) => {
  const previousResults = useRef(searchResults);

  // useEffect para detectar novos resultados e expandir automaticamente
  useEffect(() => {
    const currentResults = searchResults;
    const prevResults = previousResults.current;
    
    // Verificar se há um novo resultado smartSearch
    if (currentResults.smartSearch && 
        (!prevResults.smartSearch || 
         currentResults.smartSearch.timestamp !== prevResults.smartSearch.timestamp)) {
      
      console.log('🎯 useEffect: Novo resultado smartSearch detectado, expandindo...');
      
      if (currentResults.smartSearch.found && !expandedResults.smartSearch) {
        setTimeout(() => {
          toggleExpandedResult('smartSearch');
        }, 10);
      }
    }
    
    // Atualizar referência
    previousResults.current = currentResults;
  }, [searchResults, expandedResults, toggleExpandedResult]);

  // Função para lidar com tecla Enter
  const handleKeyPress = (e, apiKey) => {
    if (e.key === 'Enter') {
      searchCobranca(apiKey);
    }
  };

  // Função para tratar resultados da busca inteligente
  const handleSmartSearchResult = (apiKey, result) => {
    console.log('🎯 SearchPage: handleSmartSearchResult chamado', { apiKey, result });
    
    // Garantir que o resultado tenha uma chave única para forçar re-render
    const resultWithTimestamp = {
      ...result,
      timestamp: Date.now()
    };
    
    // Atualizar os resultados (o useEffect cuidará da expansão)
    setSearchResults(prev => ({
      ...prev,
      [apiKey]: resultWithTimestamp
    }));
  };

  // Verificar se há resultados para determinar o layout
  const hasResults = Object.keys(searchResults).length > 0;
  
  // Verificar se o usuário é 'user' - mostrar apenas busca inteligente
  const isBasicUser = user?.role === 'user';
  
  return (
    <div className={hasResults ? "h-screen flex flex-col" : "space-y-6"}>
      {/* Header da página */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={hasResults ? "text-center mb-4" : "text-center"}
      >
        <div className={hasResults ? "flex items-center justify-center mb-2" : "flex items-center justify-center mb-4"}>
          <div className={hasResults ? "p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg mr-3" : "p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg mr-4"}>
            <Search className={hasResults ? "w-6 h-6 text-white" : "w-8 h-8 text-white"} />
          </div>
          <div>
            <h1 className={hasResults ? "text-2xl font-bold text-gray-900" : "text-3xl font-bold text-gray-900"}>
              {isBasicUser ? 'Consulta Inteligente' : 'Consulta de Cobrança'}
            </h1>
            <p className="text-gray-600">
              {isBasicUser ? 'Busque por CNPJ, CPF ou Código PC' : 'Busque por ID em todas as APIs do sistema'}
            </p>
          </div>
        </div>
        
        {/* Descrição da busca unificada - Esconder para usuários básicos */}
        {!isBasicUser && (
          <div className={hasResults ? "max-w-2xl mx-auto p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200" : "max-w-2xl mx-auto p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200"}>
            <div className="flex items-center mb-3">
              <Zap className="w-5 h-5 text-blue-600 mr-2" />
              <h3 className="font-semibold text-blue-800">Busca Inteligente Unificada</h3>
            </div>
            <p className={hasResults ? "text-xs text-blue-700 mb-2" : "text-sm text-blue-700 mb-3"}>
              O sistema testa automaticamente diferentes métodos de busca para encontrar os dados:
            </p>
            <div className={hasResults ? "grid grid-cols-1 md:grid-cols-3 gap-2 text-xs" : "grid grid-cols-1 md:grid-cols-3 gap-3 text-xs"}>
              <div className={hasResults ? "bg-blue-100 p-1 rounded" : "bg-blue-100 p-2 rounded"}>
                <div className="font-medium text-blue-800">🔍 Parcelas</div>
                <div className="text-blue-600">Testa PC-XXXXX-1-1 até PC-XXXXX-1-5</div>
              </div>
              <div className={hasResults ? "bg-green-100 p-1 rounded" : "bg-green-100 p-2 rounded"}>
                <div className="font-medium text-green-800">🎯 Direto</div>
                <div className="text-green-600">Busca exata pelo ID informado</div>
              </div>
              <div className={hasResults ? "bg-purple-100 p-1 rounded" : "bg-purple-100 p-2 rounded"}>
                <div className="font-medium text-purple-800">📋 Padrão</div>
                <div className="text-purple-600">Lista arquivos e filtra por padrão</div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Layout de duas colunas */}
      <div className={hasResults ? "flex-1 flex" : "grid grid-cols-1 xl:grid-cols-3 gap-6"}>
        {/* Coluna da Esquerda - Painéis de Busca */}
        <div className={hasResults ? "w-1/4 pr-2" : "xl:col-span-1"}>
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            {/* Painel de Busca Inteligente - Nova Funcionalidade */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.05 }}
            >
              <SmartSearchPanel onSearchResult={handleSmartSearchResult} />
            </motion.div>

            {/* Painéis de busca individuais - Esconder para usuários básicos */}
            {!isBasicUser && Object.entries(APIs).map(([apiKey, api]) => (
              <motion.div
                key={apiKey}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 * (Object.keys(APIs).indexOf(apiKey) + 1) }}
              >
                {/* Painel de busca original */}
                <SearchPanel
                  apiKey={apiKey}
                  api={api}
                  searchInput={searchInputs[apiKey] || ''}
                  setSearchInput={(value) => setSearchInputs(prev => ({ ...prev, [apiKey]: value }))}
                  onSearch={() => searchCobranca(apiKey)}
                  loading={loading[apiKey] || false}
                  onKeyPress={(e) => handleKeyPress(e, apiKey)}
                />
                
                {/* Resultado resumido da busca */}
                {searchResults[apiKey] && (
                  <SearchResult searchResult={searchResults[apiKey]} />
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Coluna da Direita - Resultados Detalhados */}
        <div className={hasResults ? "w-3/4" : "xl:col-span-2"}>
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <ResultsPanel 
              searchResults={searchResults}
              expandedResults={expandedResults}
              toggleExpandedResult={toggleExpandedResult}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
